import { Select } from "@mantine/core";
import { useAtom, useSetAtom } from "jotai";
import { useEffect } from "react";
import { TFunction } from "react-i18next";
import { colors, pxToRem } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import Image from "components/Image";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { useGetAccidentResponsibility } from "app/hooks/useGetAccidentResponsibility";
import {
  accidentResponsibilityAtom,
  otherVehicleCircumstanceAtom,
  vehicleCircumstancePhAtom,
} from "atoms/accidentCircumstance";
import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { AccidentResponsibility, Circumstance } from "types/DigitalJourneySession";

export const DJAccidentCircumstancePage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [vehicleCircumstancePh, setVehicleCircumstancePh] = useAtom(vehicleCircumstancePhAtom);
  const [otherVehicleCircumstance, setOtherVehicleCircumstance] = useAtom(otherVehicleCircumstanceAtom);
  const [accidentResponsibility, setAccidentResponsibility] = useAtom(accidentResponsibilityAtom);
  const setVehiclePhotos = useSetAtom(vehiclePhotosAtom);
  const setDamagePhotos = useSetAtom(damagePhotosAtom);
  const setSelectedBodyShop = useSetAtom(bodyShopAtom);
  const setClaimHandlingDecision = useSetAtom(claimHandlingDecisionAtom);

  const data = useGetAccidentResponsibility(vehicleCircumstancePh, otherVehicleCircumstance);

  const isDisabled =
    vehicleCircumstancePh === null ||
    otherVehicleCircumstance === null ||
    accidentResponsibility === null ||
    accidentResponsibility === AccidentResponsibility.NC;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Accident Circumstance - continue" });
    if (accidentResponsibility === AccidentResponsibility.T) {
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    }
    onNext();
  };

  useEffect(() => {
    if (data && data.responsibility && data.responsibility !== accidentResponsibility) {
      setAccidentResponsibility(data.responsibility);
    }
  }, [data]);

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.accident_circumstance.title")}
        </Text>
        <Text>{t("digital_journey.accident_circumstance.description")}</Text>
        <Select
          name="vehicleCircumstancePh"
          label={t("digital_journey.accident_circumstance.ph_vehicle_label")}
          placeholder={t("digital_journey.accident_circumstance.select_placeholder")}
          data={getVehicleCircumstanceOptions(t).reverse()}
          value={vehicleCircumstancePh}
          onChange={(value) => {
            setVehicleCircumstancePh(value as Circumstance);
            setAccidentResponsibility(null);
          }}
        />
        <Select
          name="otherVehicleCircumstance"
          label={t("digital_journey.accident_circumstance.other_vehicle_label")}
          placeholder={t("digital_journey.accident_circumstance.select_placeholder")}
          data={getVehicleCircumstanceOptions(t).reverse()}
          value={otherVehicleCircumstance}
          onChange={(value) => {
            setOtherVehicleCircumstance(value as Circumstance);
            setAccidentResponsibility(null);
          }}
        />
        {accidentResponsibility && (
          <Flex
            mt={24}
            p={16}
            gap={8}
            direction="row"
            align="flex-start"
            style={{
              background: "#EDF5FF",
              border: "1px",
              borderColor: colors.blue[10],
              borderRadius: pxToRem[8],
            }}
          >
            <Image
              style={{ marginTop: pxToRem[2] }}
              width={14}
              src="/assets/svg/info-icon.svg"
              alt="Accident Circumstance Info"
            />
            <Text>{getDecisionLabel(t, accidentResponsibility)}</Text>
          </Flex>
        )}
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};

export const getVehicleCircumstanceOptions = (t: TFunction) => {
  return [
    {
      label: t("digital_journey.accident_circumstance.failure_to_observe_signal"),
      value: Circumstance.FAILURE_TO_OBSERVE_SIGNAL,
    },
    { label: t("digital_journey.accident_circumstance.coming_from_right"), value: Circumstance.COMING_FROM_RIGHT },
    { label: t("digital_journey.accident_circumstance.wrong_way_on_road"), value: Circumstance.WRONG_WAY_ON_ROAD },
    { label: t("digital_journey.accident_circumstance.reversing"), value: Circumstance.REVERSING },
    { label: t("digital_journey.accident_circumstance.turning_left"), value: Circumstance.TURNING_LEFT },
    { label: t("digital_journey.accident_circumstance.turning_right"), value: Circumstance.TURNING_RIGHT },
    { label: t("digital_journey.accident_circumstance.overtaking"), value: Circumstance.OVERTAKING },
    { label: t("digital_journey.accident_circumstance.changing_lane"), value: Circumstance.CHANGING_LANE },
    {
      label: t("digital_journey.accident_circumstance.moving_in_same_direction_different_lane"),
      value: Circumstance.MOVING_IN_SAME_DIRECTION_DIFFERENT_LANE,
    },
    { label: t("digital_journey.accident_circumstance.rear_end_collision"), value: Circumstance.REAR_END_COLLISION },
    {
      label: t("digital_journey.accident_circumstance.circulating_in_roundabout"),
      value: Circumstance.CIRCULATING_IN_ROUNDABOUT,
    },
    { label: t("digital_journey.accident_circumstance.entering_roundabout"), value: Circumstance.ENTERING_ROUNDABOUT },
    {
      label: t("digital_journey.accident_circumstance.entering_private_area"),
      value: Circumstance.ENTERING_PRIVATE_AREA,
    },
    {
      label: t("digital_journey.accident_circumstance.exiting_private_area"),
      value: Circumstance.EXITING_PRIVATE_AREA,
    },
    { label: t("digital_journey.accident_circumstance.parking"), value: Circumstance.PARKING },
    { label: t("digital_journey.accident_circumstance.starting_after_stop"), value: Circumstance.STARTING_AFTER_STOP },
    { label: t("digital_journey.accident_circumstance.stationary_vehicle"), value: Circumstance.STATIONARY_VEHICLE },
    { label: t("digital_journey.accident_circumstance.other"), value: Circumstance.OTHER },
  ];
};

const getDecisionLabel = (t: TFunction, responsibility: AccidentResponsibility) => {
  switch (responsibility) {
    case AccidentResponsibility.T:
      return t("digital_journey.accident_circumstance.ph_responsible_decision");
    case AccidentResponsibility.R:
      return t("digital_journey.accident_circumstance.other_driver_responsible_decision");
    case AccidentResponsibility.C:
      return t("digital_journey.accident_circumstance.both_drivers_responsible_decision");
    case AccidentResponsibility.NC:
      return t("digital_journey.accident_circumstance.impossible_decision");
  }
};
